(() => {
  const PRODUCT_HANDLE = 'outdoor-artificial-hydrangea-flower-bundles';
  const PRODUCT_ID = '10265665864001';
  const QSC_SELECT_SELECTOR = 'select.qsc2-variant-opt__select';
  const NATIVE_RADIO_SELECTOR = 'variant-selects input[type="radio"]';
  const MAIN_PRODUCT_SELECTOR = `product-info[data-product-id="${PRODUCT_ID}"]`;
  const MAIN_IMAGE_WIDTH = 990;

  let productPromise;
  let lastVariantId = null;
  let updateTimer;
  let isSyncingQsc = false;

  const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const isTargetProductPage = () =>
    window.location.pathname.includes(`/products/${PRODUCT_HANDLE}`) ||
    Boolean(document.querySelector(MAIN_PRODUCT_SELECTOR));

  const getProductInfo = () => document.querySelector(MAIN_PRODUCT_SELECTOR);

  const loadProduct = () => {
    if (!productPromise) {
      productPromise = fetch(`/products/${PRODUCT_HANDLE}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      }).then((response) => {
        if (!response.ok) throw new Error(`Product JSON failed: ${response.status}`);
        return response.json();
      });
    }
    return productPromise;
  };

  const getVisibleQscSelects = () =>
    Array.from(document.querySelectorAll(QSC_SELECT_SELECTOR)).filter((select) => {
      const rect = select.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && !select.disabled;
    });

  const getQscOptionSelects = () => {
    const selects = getVisibleQscSelects();
    const colorSelect = selects.find((select) => /color/i.test(select.id) || /color/i.test(select.closest('label')?.innerText || '')) || selects[0];
    const quantitySelect =
      selects.find((select) => /quantity|bundle|discount/i.test(select.id) || /quantity|bundle|discount/i.test(select.closest('label')?.innerText || '')) ||
      selects[1];

    return { colorSelect, quantitySelect };
  };

  const getSelectedOptions = () => {
    const { colorSelect, quantitySelect } = getQscOptionSelects();

    if (!colorSelect || !quantitySelect) return null;
    return [normalize(colorSelect.value), normalize(quantitySelect.value)];
  };

  const getNativeFieldset = (pattern) =>
    Array.from(document.querySelectorAll('variant-selects fieldset.product-form__input')).find((fieldset) => {
      const legend = normalize(fieldset.querySelector('legend')?.textContent);
      return pattern.test(legend);
    });

  const getCheckedNativeValue = (fieldset) => normalize(fieldset?.querySelector('input[type="radio"]:checked')?.value);

  const ensureColorMixQuantity = () => {
    const colorFieldset = getNativeFieldset(/^color$/i);
    const quantityFieldset = getNativeFieldset(/quantity|bundle|discount/i);
    const selectedColor = getCheckedNativeValue(colorFieldset);
    if (!selectedColor.includes('Color Mix') || !quantityFieldset) return false;

    const targetQuantity = Array.from(quantityFieldset.querySelectorAll('input[type="radio"]')).find((input) =>
      normalize(input.value).includes('8 bundles')
    );
    if (!targetQuantity || targetQuantity.checked) return false;

    targetQuantity.checked = true;
    return true;
  };

  const getNativeSelectedOptions = () => {
    const colorFieldset = getNativeFieldset(/^color$/i);
    const quantityFieldset = getNativeFieldset(/quantity|bundle|discount/i);
    const color = getCheckedNativeValue(colorFieldset);
    const quantity = getCheckedNativeValue(quantityFieldset);
    return color && quantity ? [color, quantity] : null;
  };

  const findVariant = (product, selectedOptions) =>
    product.variants.find((variant) => {
      const options = (variant.options || []).map(normalize);
      return options[0] === selectedOptions[0] && options[1] === selectedOptions[1];
    });

  const imageUrlWithWidth = (src, width) => {
    if (!src) return null;
    const sourceUrl = new URL(src, window.location.origin);
    const fileMarker = '/files/';
    const fileIndex = sourceUrl.pathname.lastIndexOf(fileMarker);
    if (fileIndex !== -1) {
      const fileName = sourceUrl.pathname.slice(fileIndex + fileMarker.length);
      const storefrontUrl = new URL(`/cdn/shop/files/${fileName}`, window.location.origin);
      const version = sourceUrl.searchParams.get('v');
      if (version) storefrontUrl.searchParams.set('v', version);
      storefrontUrl.searchParams.set('width', String(width));
      return storefrontUrl.toString();
    }
    sourceUrl.searchParams.set('width', String(width));
    return sourceUrl.toString();
  };

  const preloadVariantImage = (variant) => {
    const src = imageUrlWithWidth(variant?.featured_image?.src || variant?.featured_media?.preview_image?.src, MAIN_IMAGE_WIDTH);
    if (!src) return;
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'high';
    image.src = src;
  };

  const syncVariantIdInputs = (productInfo, variantId) => {
    if (typeof productInfo.updateVariantInputs === 'function') {
      productInfo.updateVariantInputs(variantId);
      return;
    }
    productInfo.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
      input.value = variantId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  const syncUrl = (productInfo, variantId) => {
    if (typeof productInfo.updateURL === 'function') {
      productInfo.updateURL(productInfo.dataset.url || `/products/${PRODUCT_HANDLE}`, variantId);
    } else {
      window.history.replaceState({}, '', `/products/${PRODUCT_HANDLE}?variant=${variantId}`);
    }
  };

  const setQscSelectValue = (select, value) => {
    if (!select) return;
    const targetOption = Array.from(select.options || []).find((option) => normalize(option.value) === normalize(value));
    if (!targetOption || select.value === targetOption.value) return;

    select.value = targetOption.value;
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const syncQscSelects = (selectedOptions) => {
    const { colorSelect, quantitySelect } = getQscOptionSelects();
    if (!colorSelect || !quantitySelect || !selectedOptions?.length) return;

    isSyncingQsc = true;
    try {
      setQscSelectValue(colorSelect, selectedOptions[0]);
      setQscSelectValue(quantitySelect, selectedOptions[1]);
    } finally {
      window.setTimeout(() => {
        isSyncingQsc = false;
      }, 0);
    }
  };

  const syncQscFromNative = () => {
    const selectedOptions = getNativeSelectedOptions();
    if (selectedOptions) syncQscSelects(selectedOptions);
  };

  const fetchSelectedSectionHtml = async (productInfo, variantId) => {
    const sectionId = productInfo.dataset.section;
    const productUrl = productInfo.dataset.url || `/products/${PRODUCT_HANDLE}`;
    const response = await fetch(`${productUrl}?variant=${variantId}&section_id=${sectionId}`, {
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Section HTML failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  const updateGallery = async (variant) => {
    const productInfo = getProductInfo();
    if (!productInfo || !variant?.id || variant.id === lastVariantId) return;

    const mediaId = variant.featured_media?.id || variant.featured_image?.id;
    const selectedOptions = (variant.options || []).map(normalize);
    lastVariantId = variant.id;
    preloadVariantImage(variant);
    syncVariantIdInputs(productInfo, variant.id);
    syncUrl(productInfo, variant.id);
    syncQscSelects(selectedOptions);

    if (!mediaId) return;

    try {
      const html = await fetchSelectedSectionHtml(productInfo, variant.id);
      if (typeof productInfo.updateMedia === 'function') {
        productInfo.updateMedia(html, mediaId);
      } else {
        productInfo.querySelector('media-gallery')?.setActiveMedia?.(`${productInfo.dataset.section}-${mediaId}`, true);
      }
      document.dispatchEvent(
        new CustomEvent('tafren:variant-media-bridge:updated', {
          bubbles: true,
          detail: { variantId: variant.id, mediaId },
        })
      );
    } catch (error) {
      console.warn('[Tafren] Variant media bridge failed to update gallery', error);
    }
  };

  const scheduleUpdateFromQsc = () => {
    if (!isTargetProductPage() || isSyncingQsc) return;
    window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(async () => {
      const selectedOptions = getSelectedOptions();
      if (!selectedOptions) return;
      try {
        const product = await loadProduct();
        const variant = findVariant(product, selectedOptions);
        if (variant) await updateGallery(variant);
      } catch (error) {
        console.warn('[Tafren] Variant media bridge could not resolve selected variant', error);
      }
    }, 80);
  };

  const scheduleUpdateFromNative = () => {
    if (!isTargetProductPage()) return;
    window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(async () => {
      const selectedOptions = getNativeSelectedOptions();
      if (!selectedOptions) return;
      try {
        const product = await loadProduct();
        const variant = findVariant(product, selectedOptions);
        if (variant) await updateGallery(variant);
      } catch (error) {
        console.warn('[Tafren] Native variant media bridge could not resolve selected variant', error);
      }
    }, 80);
  };

  document.addEventListener(
    'change',
    (event) => {
      if (event.target?.matches?.(NATIVE_RADIO_SELECTOR)) ensureColorMixQuantity();
    },
    true
  );

  document.addEventListener('change', (event) => {
    if (event.target?.matches?.(QSC_SELECT_SELECTOR)) scheduleUpdateFromQsc();
  });

  document.addEventListener('change', (event) => {
    if (event.target?.matches?.(NATIVE_RADIO_SELECTOR)) scheduleUpdateFromNative();
  });

  document.addEventListener('DOMContentLoaded', () => {
    if (!isTargetProductPage()) return;
    ensureColorMixQuantity();
    syncQscFromNative();
    window.setTimeout(syncQscFromNative, 500);
    window.setTimeout(syncQscFromNative, 1500);
    document.querySelectorAll(QSC_SELECT_SELECTOR).forEach((select) => {
      select.addEventListener('input', scheduleUpdateFromQsc);
    });
    document.querySelectorAll(NATIVE_RADIO_SELECTOR).forEach((input) => {
      input.addEventListener('input', scheduleUpdateFromNative);
    });
  });
})();
